/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError, Password } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import { AccountService, Invitation, LoginChallenge } from "App/Contracts";

import { ProcessException } from "./Base";

/* Public activation / login flow. No auth token is attached (these run before a session exists);
   the backend exposes the endpoints outside the "/Internal/" guard. */
export default class Account implements AccountService {
    private invitationService: Feathers.Service<any>;
    private activateService: Feathers.Service<any>;
    private loginService: Feathers.Service<any>;
    private confirmTotpService: Feathers.Service<any>;
    private forgotPasswordService: Feathers.Service<any>;
    private resetTokenService: Feathers.Service<any>;
    private resetPasswordService: Feathers.Service<any>;

    constructor(
        invitationService: Feathers.Service<any>,
        activateService: Feathers.Service<any>,
        loginService: Feathers.Service<any>,
        confirmTotpService: Feathers.Service<any>,
        forgotPasswordService: Feathers.Service<any>,
        resetTokenService: Feathers.Service<any>,
        resetPasswordService: Feathers.Service<any>
    ) {
        this.invitationService = invitationService;
        this.activateService = activateService;
        this.loginService = loginService;
        this.confirmTotpService = confirmTotpService;
        this.forgotPasswordService = forgotPasswordService;
        this.resetTokenService = resetTokenService;
        this.resetPasswordService = resetPasswordService;
    }

    @Autobind
    async Login(
        email: string,
        password: Password
    ): Promise<[ LoginChallenge | undefined, CustomError | undefined ]> {
        try {
            const results = await this.loginService.create({
                Email: email,
                Password: password.Value
            });

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            return [
                {
                    Status: results.Status,
                    ManualKey: results.ManualKey,
                    KeyUri: results.KeyUri,
                    Account: results.Account,
                    Issuer: results.Issuer
                },
                undefined
            ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async ConfirmTotp(
        email: string,
        password: Password,
        code: string
    ): Promise<[ string[] | undefined, CustomError | undefined ]> {
        try {
            const results = await this.confirmTotpService.create({
                Email: email,
                Password: password.Value,
                Code: code
            });

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            /* The backend joins the codes into a comma-separated string (the generic formatter
               mangles arrays of scalars), so split it back here. */
            const raw = results.RecoveryCodes;
            const codes: string[] = typeof raw === "string" && raw !== ""
                ? raw.split( "," )
                : ( Array.isArray( raw ) ? raw : [] );

            return [ codes, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async GetInvitation(
        token: string
    ): Promise<[ Invitation | undefined, CustomError | undefined ]> {
        try {
            const results = await this.invitationService.get( token );

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            return [
                {
                    FirstName: results.FirstName !== undefined ? results.FirstName : "",
                    Email: results.Email !== undefined ? results.Email : ""
                },
                undefined
            ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Activate( token: string, password: Password ): Promise<CustomError | undefined> {
        try {
            const results = await this.activateService.create({
                Token: token,
                Password: password.Value
            });

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    @Autobind
    async ForgotPassword( email: string ): Promise<CustomError | undefined> {
        try {
            const results = await this.forgotPasswordService.create({ Email: email });

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }

    @Autobind
    async GetResetToken(
        token: string
    ): Promise<[ Invitation | undefined, CustomError | undefined ]> {
        try {
            const results = await this.resetTokenService.get( token );

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            if( results === undefined || results.Valid !== true )
                return [ undefined, undefined ];

            return [
                {
                    FirstName: results.FirstName !== undefined ? results.FirstName : "",
                    Email: results.Email !== undefined ? results.Email : ""
                },
                undefined
            ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async ResetPassword( token: string, password: Password ): Promise<CustomError | undefined> {
        try {
            const results = await this.resetPasswordService.create({
                Token: token,
                Password: password.Value
            });

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }

        return undefined;
    }
}
